import React from 'react';
import { Users, User, Star, Calendar, CheckCircle, Clock, Award } from 'lucide-react';

const JuryDetails = ({ jury }) => {
  if (!jury) return null;

  const getStatutColor = (statut) => {
    const colors = {
      'CONSTITUE': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'VALIDE': 'bg-blue-100 text-blue-800 border-blue-200', 
      'ACTIF': 'bg-green-100 text-green-800 border-green-200',
      'ARCHIVE': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[statut] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatutIcon = (statut) => {
    switch(statut) {
      case 'CONSTITUE': return <Calendar className="h-4 w-4" />;
      case 'VALIDE': return <CheckCircle className="h-4 w-4" />;
      case 'ACTIF': return <CheckCircle className="h-4 w-4" />;
      case 'ARCHIVE': return <Users className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'RAPPORTEUR': 'bg-purple-100 text-purple-800',
      'EXAMINATEUR': 'bg-blue-100 text-blue-800',
      'DIRECTEUR': 'bg-green-100 text-green-800',
      'CODIRECTEUR': 'bg-teal-100 text-teal-800',
      'INVITE': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-purple-600 p-3 rounded-lg">
              <Users className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{jury.nom}</h2>
              <p className="text-lg text-gray-600">{jury.type_soutenance?.nom}</p>
            </div>
          </div>
          <div className={`inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-full border ${getStatutColor(jury.statut)}`}>
            {getStatutIcon(jury.statut)}
            <span>{jury.statut}</span>
          </div>
        </div>
      </div>

      {/* Informations générales */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <Award className="h-5 w-5 text-gray-600" />
          <span>Informations générales</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Type de soutenance</label>
            <p className="text-gray-900">{jury.type_soutenance?.nom || 'Non défini'}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Date de constitution</label>
            <p className="text-gray-900 flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span>{new Date(jury.date_constitution).toLocaleDateString('fr-FR')}</span>
            </p>
          </div>

          {jury.type_soutenance && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Durée prévue</label>
                <p className="text-gray-900 flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span>{jury.type_soutenance.duree_minutes} minutes</span>
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Nombre de membres recommandé</label>
                <p className="text-gray-900">
                  {jury.type_soutenance.nombre_jury_min} - {jury.type_soutenance.nombre_jury_max} membres
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Président */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <Star className="h-5 w-5 text-amber-500" />
          <span>Président du jury</span>
        </h3>
        
        {jury.president_info ? (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-900">{jury.president_info.nom}</p>
                <p className="text-sm text-gray-600">{jury.president_info.email}</p>
                <p className="text-sm text-amber-700 font-medium mt-1">
                  {jury.president_info.type === 'interne' ? 'Enseignant interne' : 'Enseignant externe'}
                </p>
              </div>
              <div className="bg-amber-200 p-2 rounded-lg">
                <Star className="h-6 w-6 text-amber-700" />
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
            <p className="text-gray-500">Aucun président désigné</p>
          </div>
        )}
      </div>

      {/* Membres */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <User className="h-5 w-5 text-blue-500" />
            <span>Membres du jury</span>
          </h3>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {jury.nombre_membres || 0} membre{(jury.nombre_membres || 0) > 1 ? 's' : ''}
          </span>
        </div>
        
        {jury.membres_jury && jury.membres_jury.length > 0 ? (
          <div className="space-y-3">
            {jury.membres_jury.map((membre, index) => (
              <div key={index} className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-200 p-2 rounded-lg">
                        <User className="h-4 w-4 text-blue-700" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {membre.enseignant_info?.nom || 'Nom non disponible'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {membre.enseignant_info?.email}
                        </p>
                        <p className="text-sm text-gray-500">
                          {membre.enseignant_info?.type === 'interne' ? 'Enseignant interne' : 'Enseignant externe'}
                          {membre.enseignant_info?.type === 'interne' && membre.enseignant_info?.departement && 
                            ` - ${membre.enseignant_info.departement}`
                          }
                          {membre.enseignant_info?.type === 'externe' && membre.enseignant_info?.etablissement && 
                            ` - ${membre.enseignant_info.etablissement}`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(membre.role)}`}>
                    {membre.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 p-8 rounded-lg text-center">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Aucun membre ajouté à ce jury</p>
          </div>
        )}
      </div>

      {/* Description du type de soutenance */}
      {jury.type_soutenance?.description && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
          <p className="text-gray-700 leading-relaxed">{jury.type_soutenance.description}</p>
        </div>
      )}
    </div>
  );
};

export default JuryDetails; 